import { useEffect } from 'react';
import { getCookie } from 'cookies-next';

import { useGetConfigByKey } from '@/entities/regional-configuration/hooks/useGetConfigByKey';
import { START_INFO_UPDATE, TrackingCategory } from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { appLogger } from '@/utils/app-logger/appLogger';
import { DEVICE_ID_COOKIE, SESSION_ID_COOKIE } from '@/utils/constants/common';
import { isPlaceholderDeviceId } from '@/utils/deviceId';

import useTrackingProvider from '../providers/useTrackingProvider';

// Emission is tracked separately from the startinfoupdate BootstrapSignal: the signal is a
// pageview gate that must release promptly even when the start call fails or is skipped,
// while the emission may still happen later — a late client-side platform start (e.g. the
// first-ever load landing on the static 404 page) flips startSuccess after the gate was
// already released (CBSENTWEB-4936). Module-level on purpose: the signal store is global,
// so the emission guard must survive remounts too.
let hasSettledStartInfo = false;

/** @internal Test-only reset for the module-level emission guard. */
export const resetTrackPlatformStartForTests = (): void => {
    hasSettledStartInfo = false;
};

export const useTrackPlatformStart = (): void => {
    const shouldTrack = useGetConfigByKey<boolean>(
        'options.trackingConfig.shouldTrack.startinfoupdate',
    );
    const trackingProvider = useTrackingProvider();
    const startSuccess = trackingProvider?.trackingData?.startSuccess;
    useEffect(() => {
        if (hasSettledStartInfo) return;

        // Flag explicitly OFF — release the pageview gate without emission.
        if (shouldTrack === false) {
            hasSettledStartInfo = true;
            BootstrapSignals.mark(BootstrapSignalKeys.START_INFO_UPDATE);
            return;
        }

        // Start call did not succeed (skipped/failed): release the pageview gate without
        // emission, but stay unsettled — if a late client-side start succeeds, this effect
        // re-runs with startSuccess=true and emits then.
        if (shouldTrack && startSuccess === false) {
            BootstrapSignals.mark(BootstrapSignalKeys.START_INFO_UPDATE);
            return;
        }

        // shouldTrack === undefined (loading) OR true but startSuccess not ready yet.
        if (!shouldTrack || !startSuccess) return;
        hasSettledStartInfo = true;

        const deviceId = getCookie(DEVICE_ID_COOKIE);
        const sessionId = getCookie(SESSION_ID_COOKIE);

        // Log the fpid going into app{}. Placeholder here = all-zero, which gets the event rejected.
        appLogger.info('startInfoUpdate identity snapshot', {
            component: 'useTrackPlatformStart',
            sessionId: sessionId?.toString(),
            fpidClass: isPlaceholderDeviceId(deviceId as string | undefined)
                ? 'placeholder'
                : deviceId
                  ? 'valid'
                  : 'absent',
            hasSessionCookie: Boolean(sessionId),
        });

        void BootstrapSignals.whenAll([BootstrapSignalKeys.APP_INIT]).then(() => {
            void TrackerEngine.update({
                type: START_INFO_UPDATE,
                detail: {
                    // No device id: omit identity. Sending fpid: undefined degrades the fpid
                    // dataHub already holds and AEP rejects the event (EXEG-0103-400).
                    ...(deviceId && { identity: { fpid: deviceId } }),
                    appInfo: { stitcherSessionId: sessionId },
                },
                category: TrackingCategory.App.OPERATION,
            });
            BootstrapSignals.mark(BootstrapSignalKeys.START_INFO_UPDATE);
        });
    }, [startSuccess, shouldTrack]);
};
